import { read, utils, type WorkBook, type WorkSheet } from 'xlsx';
import { SERIES_SHEETS, SOURCE_PRIORITY } from '../config/kpi';
import type { BscDataBundle, RegistryRow, ValueRow } from '../types/bsc';
import { toIsoDate } from '../lib/dates';
import { toNumber } from '../lib/numbers';
import { cleanText, normalizeHeader } from '../lib/text';

const REGISTRY_FIELDS: Record<string, keyof RegistryRow> = {
  codice: 'Codice',
  nome: 'Nome',
  prospettiva_bsc: 'Prospettiva_BSC',
  descrizione_kpi: 'Descrizione_KPI',
  obiettivo_strategico_collegato: 'Obiettivo_Strategico_Collegato',
  responsabile: 'Responsabile',
  formula_di_calcolo: 'Formula_di_Calcolo',
  unita_di_misura: 'Unità_di_Misura',
  raggruppamento: 'Raggruppamento',
  fonte_dati: 'Fonte_Dati',
  frequenza_rilevazione: 'Frequenza_Rilevazione',
  valore_baseline: 'Valore_Baseline',
  soglia_minima: 'Soglia_Minima',
  target: 'Target',
  soglia_eccellenza: 'Soglia_Eccellenza',
  trend_desiderato: 'Trend_Desiderato',
  metodo_raccolta_dato: 'Metodo_Raccolta_Dato',
  validatore_dato: 'Validatore_Dato',
  rischi_di_misurazione: 'Rischi_di_misurazione',
  note: 'Note',
  azioni_correttive: 'Azioni_correttive',
};

const REGISTRY_TEXT_FIELDS = new Set<keyof RegistryRow>([
  'Codice',
  'Nome',
  'Prospettiva_BSC',
  'Descrizione_KPI',
  'Obiettivo_Strategico_Collegato',
  'Responsabile',
  'Formula_di_Calcolo',
  'Unità_di_Misura',
  'Raggruppamento',
  'Fonte_Dati',
  'Frequenza_Rilevazione',
  'Trend_Desiderato',
  'Metodo_Raccolta_Dato',
  'Validatore_Dato',
  'Rischi_di_misurazione',
  'Note',
  'Azioni_correttive',
]);

function workbookFromBuffer(buffer: ArrayBuffer): WorkBook {
  return read(buffer, {
    type: 'array',
    cellDates: true,
    raw: true,
    dense: false,
  });
}

function getSheet(workbook: WorkBook, preferredName: string): WorkSheet | null {
  const exact = workbook.Sheets[preferredName];
  if (exact) return exact;

  const caseInsensitive = workbook.SheetNames.find(
    (name) => name.toLocaleLowerCase('it-IT') === preferredName.toLocaleLowerCase('it-IT'),
  );
  if (caseInsensitive) return workbook.Sheets[caseInsensitive] ?? null;

  return null;
}

function sheetRows(sheet: WorkSheet): Record<string, unknown>[] {
  return utils.sheet_to_json<Record<string, unknown>>(sheet, {
    defval: null,
    raw: true,
  });
}

function normalizeRawRow(row: Record<string, unknown>): Record<string, unknown> {
  const normalized: Record<string, unknown> = Object.create(null) as Record<string, unknown>;
  for (const [key, value] of Object.entries(row)) {
    normalized[normalizeHeader(key)] = value;
  }
  return normalized;
}

function emptyRegistryRow(): RegistryRow {
  return {
    Codice: '',
    Nome: '',
    Prospettiva_BSC: '',
    Descrizione_KPI: '',
    Obiettivo_Strategico_Collegato: '',
    Responsabile: '',
    Formula_di_Calcolo: '',
    Unità_di_Misura: '',
    Raggruppamento: '',
    Fonte_Dati: '',
    Frequenza_Rilevazione: '',
    Valore_Baseline: null,
    Soglia_Minima: null,
    Target: null,
    Soglia_Eccellenza: null,
    Trend_Desiderato: '',
    Metodo_Raccolta_Dato: '',
    Validatore_Dato: '',
    Rischi_di_misurazione: '',
    Note: '',
    Azioni_correttive: '',
  };
}

export function parseRegistryWorkbook(buffer: ArrayBuffer): RegistryRow[] {
  const workbook = workbookFromBuffer(buffer);
  const sheet = getSheet(workbook, 'kpi_registry') ?? workbook.Sheets[workbook.SheetNames[0]];
  if (!sheet) {
    throw new Error('Il file registry non contiene fogli leggibili.');
  }

  return sheetRows(sheet)
    .map((raw) => {
      const output = emptyRegistryRow();
      for (const [rawKey, rawValue] of Object.entries(raw)) {
        const field = REGISTRY_FIELDS[normalizeHeader(rawKey)];
        if (!field) continue;
        output[field] = REGISTRY_TEXT_FIELDS.has(field) ? cleanText(rawValue) : rawValue;
      }
      output.Codice = output.Codice.toUpperCase();
      return output;
    })
    .filter((row) => row.Codice !== '');
}

function parseManualValues(sheet: WorkSheet, startOrder: number): ValueRow[] {
  let sourceOrder = startOrder;
  return sheetRows(sheet)
    .map(normalizeRawRow)
    .map((row): ValueRow | null => {
      const period = toIsoDate(row.period);
      const kpiCode = cleanText(row.kpi_code).toUpperCase();
      if (!period || !kpiCode) return null;

      sourceOrder += 1;
      return {
        period,
        kpi_code: kpiCode,
        value: toNumber(row.value),
        dimension_type: cleanText(row.dimension_type) || 'azienda',
        dimension_value: cleanText(row.dimension_value) || 'totale',
        source_file: cleanText(row.source_file) || null,
        note: cleanText(row.note) || null,
        record_type: 'manuale (Excel)',
        source_order: sourceOrder,
      };
    })
    .filter((row): row is ValueRow => row !== null);
}

function parseSeriesValues(
  sheet: WorkSheet,
  kpiCode: string,
  valueColumns: string[],
  defaultNote: string,
  startOrder: number,
): ValueRow[] {
  let sourceOrder = startOrder;
  const normalizedValueColumns = valueColumns.map(normalizeHeader);

  return sheetRows(sheet)
    .map(normalizeRawRow)
    .map((row): ValueRow | null => {
      const period = toIsoDate(row.period);
      const valueKey = normalizedValueColumns.find((column) => row[column] !== null && row[column] !== undefined);
      const value = valueKey ? toNumber(row[valueKey]) : null;
      const dimensionValue = cleanText(row.dimension_value) || 'totale';

      if (!period || value === null || !dimensionValue) return null;

      sourceOrder += 1;
      return {
        period,
        kpi_code: kpiCode,
        value,
        dimension_type: cleanText(row.dimension_type) || 'azienda',
        dimension_value: dimensionValue,
        source_file: cleanText(row.source_file) || null,
        note: cleanText(row.note) || defaultNote,
        record_type: 'imported',
        source_order: sourceOrder,
      };
    })
    .filter((row): row is ValueRow => row !== null);
}

function valueKey(row: ValueRow): string {
  return [
    row.kpi_code,
    row.period,
    row.dimension_type.toLocaleLowerCase('it-IT'),
    row.dimension_value.toLocaleLowerCase('it-IT'),
  ].join('\u001f');
}

export function mergeValues(rows: ValueRow[]): ValueRow[] {
  const chosen = new Map<string, ValueRow>();

  for (const row of rows) {
    const key = valueKey(row);
    const existing = chosen.get(key);
    if (!existing) {
      chosen.set(key, row);
      continue;
    }

    const existingPriority = SOURCE_PRIORITY[existing.record_type] ?? 0;
    const newPriority = SOURCE_PRIORITY[row.record_type] ?? 0;
    if (newPriority > existingPriority || (newPriority === existingPriority && row.source_order >= existing.source_order)) {
      chosen.set(key, row);
    }
  }

  return [...chosen.values()].sort((a, b) => {
    return (
      a.kpi_code.localeCompare(b.kpi_code, 'it') ||
      a.period.localeCompare(b.period) ||
      a.dimension_type.localeCompare(b.dimension_type, 'it') ||
      a.dimension_value.localeCompare(b.dimension_value, 'it') ||
      a.source_order - b.source_order
    );
  });
}

export function parseValuesWorkbook(buffer: ArrayBuffer): ValueRow[] {
  const workbook = workbookFromBuffer(buffer);
  const manualSheet = getSheet(workbook, 'kpi_values');
  if (!manualSheet) {
    throw new Error('Il file valori non contiene il foglio obbligatorio "kpi_values".');
  }

  const rows: ValueRow[] = [];
  rows.push(...parseManualValues(manualSheet, rows.length));

  for (const [kpiCode, config] of Object.entries(SERIES_SHEETS)) {
    const sheet = getSheet(workbook, kpiCode);
    if (!sheet) continue;
    rows.push(
      ...parseSeriesValues(
        sheet,
        kpiCode,
        config.valueColumns,
        config.defaultNote,
        rows.length,
      ),
    );
  }

  return mergeValues(rows);
}

export function buildBundleFromWorkbooks(
  registryBuffer: ArrayBuffer,
  valuesBuffer: ArrayBuffer,
): BscDataBundle {
  return {
    registry: parseRegistryWorkbook(registryBuffer),
    values: parseValuesWorkbook(valuesBuffer),
  };
}
